<template>
  <div class="fluid-page user-base-info">
    <b-container>
      <!-- Breadcrumb -->
      <breadcrumb class="mt-4"></breadcrumb>

      <!-- Page title wrapper -->
      <div class="page-title-wrapper">
        <b-link :to="{ name: 'users-home' }">
          <b-img :src="require('@/assets/img/icons/back.svg')" alt="Go back" />
        </b-link>
        <page-title variant="user">
          <template #title>{{ $t('pages.settings.users.editUser') }}</template>
        </page-title>
      </div>

      <slot v-if="loadingInfo">
        <div class="getting-info inside mb-0 pb-0">{{ $t('general.gettingInformation') }}</div>
      </slot>

      <!-- No data -->
      <div v-if="!loadingInfo">
        <!-- Info -->
        <div class="info">
          <b-alert :show="formResponse !== null" variant="danger">
            {{ formResponse }}
          </b-alert>

          <ValidationObserver ref="form">
            <b-form novalidate autocomplete="off">
              <div class="source-data">
                <!-- Username -->
                <ValidationProvider rules="required" name="username" v-slot="{ valid, errors }">
                  <b-form-group label="Username:" :label-class="{ 'is-invalid': errors[0] }" :invalid-feedback="errors[0]">
                    <b-form-input v-model="baseInfo.username" :state="errors[0] ? false : valid ? true : null"></b-form-input>
                  </b-form-group>
                </ValidationProvider>

                <!-- Password -->
                <ValidationProvider rules="required" name="databasePassword" v-slot="{ valid, errors }">
                  <b-form-group
                    label="Password:"
                    :label-class="{ 'is-invalid': errors[0] }"
                    :invalid-feedback="errors[0]"
                    autocomplete="new-password"
                  >
                    <b-form-input
                      type="password"
                      v-model="baseInfo.password"
                      autocomplete="new-password"
                      :state="errors[0] ? false : valid ? true : null"
                    ></b-form-input>
                  </b-form-group>
                </ValidationProvider>

                <!-- Profile -->
                <ValidationProvider rules="required" name="profile" v-slot="{ valid, errors }">
                  <b-form-group label="Profile" :label-class="{ 'is-invalid': errors[0] }" :invalid-feedback="errors[0]">
                    <div class="select-wrapper">
                      <b-form-select v-model="baseInfo.role" :state="errors[0] ? false : valid ? true : null">
                        <option value="null">Select an option</option>
                        <option v-for="item in profiles" :key="item.id" :value="item.id">{{ item.name }}</option>
                      </b-form-select>
                    </div>
                  </b-form-group>
                </ValidationProvider>

                <ValidationProvider rules="required" name="projects" v-slot="{ errors }">
                  <b-form-group :label-class="{ 'is-invalid': errors[0] }" :invalid-feedback="errors[0]">
                    <multiselect-projects v-model="baseInfo.digital_creator_projects" />
                  </b-form-group>
                </ValidationProvider>

                <ValidationProvider rules="required" name="categories" v-slot="{ errors }">
                  <b-form-group :label-class="{ 'is-invalid': errors[0] }" :invalid-feedback="errors[0]">
                    <multiselect-categories v-model="baseInfo.digital_creator_categories" />
                  </b-form-group>
                </ValidationProvider>
              </div>
            </b-form>
          </ValidationObserver>
        </div>

        <div class="call-to-action">
          <b-button @click="validateBeforeSubmit()" variant="bordered" size="lg" :disabled="sendingForm">{{ sendButtonText }}</b-button>
        </div>
      </div>
    </b-container>
  </div>
</template>

<style lang="scss" scoped>
@import '../Edition';
@import './BaseInfo';
</style>

<script>
import commonSrv from '@/api/common';
import usersSrv from '@/api/users';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import MultiselectCategories from '@/components/Forms/Categories/MultiselectCategories.vue';
import MultiselectProjects from '@/components/Forms/Projects/MultiselectProjects.vue';
import PageTitle from '@/components/PageTitle/PageTitle.vue';
import { showSuccess } from '@/components/ScreenAlert';
import breadcrumbHandler from '@/mixins/breadcrumbHandler';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

export default {
  mixins: [breadcrumbHandler],

  components: {
    ValidationObserver,
    ValidationProvider,
    Breadcrumb,
    PageTitle,
    MultiselectProjects,
    MultiselectCategories,
  },

  data() {
    return {
      loadingInfo: false,
      formResponse: null,
      baseInfo: {
        role: null,
        digital_creator_projects: [],
        digital_creator_categories: [],
      },
      sendingForm: false,
      sendButtonText: this.$t('general.send'),
      profiles: [],
    };
  },

  methods: {
    async getInfo() {
      this.loadingInfo = true;
      try {
        const baseInfo = await usersSrv.getCreator(this.$route.params.userId);
        const roles = await usersSrv.getCreatorsRoles();
        this.baseInfo = baseInfo;
        this.profiles = roles.results;
      } catch (error) {}
      this.loadingInfo = false;
    },

    validateBeforeSubmit() {
      return commonSrv.validateForm(this.$refs.form, this.handleSubmit);
    },

    async handleSubmit() {
      this.sendButtonText = this.$t('general.sending');
      this.sendingForm = true;

      try {
        await usersSrv.updateCreator(this.$route.params.userId, this.baseInfo);
        this.$router.push({ name: 'users-home' }).catch(() => {});

        showSuccess({
          confirmLabel: '',
          content: this.$t('pages.settings.users.updatedSuccessfully'),
        });
      } catch (error) {
        this.formResponse = error.message;
      }
      this.sendingForm = false;
      this.sendButtonText = this.$t('general.send');
    },
  },

  created() {
    this.setBreadcrumb([
      { text: this.$t('general.menu.home'), to: { name: 'visual-vault' } },
      { text: this.$t('general.settings'), to: { name: 'settings' } },
      { text: this.$t('pages.settings.users.title'), to: { name: 'users-home' } },
      { text: this.$t('pages.settings.users.editUser') },
    ]);
    this.getInfo();
  },
};
</script>
